import {
    REF_ID_RE,
    resolveDependencyRootId,
    resolveImportPrivateAliasId,
    scanDocumentText,
    type DocumentScanResult,
} from "@magic-md/core";

export type UnitDependencyKind =
    | "definition"
    | "for"
    | "content"
    | "import"
    | "use";

export interface UnitDependencyNode {
    id: string;
    kind: UnitDependencyKind;
    line: number;
    detail?: string;
    runnable?: boolean;
}

export interface UnitDependencyTreeNode {
    unit: UnitDependencyNode;
    children: UnitDependencyTreeNode[];
    repeated?: boolean;
    cycle?: boolean;
}

export interface UnitDependencyGraph {
    units: Map<string, UnitDependencyNode>;
    dependencies: Map<string, Set<string>>;
    dependents: Map<string, Set<string>>;
}

export interface UnitDependencyDisplayRow {
    node: UnitDependencyTreeNode;
    depth: number;
}

export interface UnitDependencyTrees {
    predecessor: UnitDependencyTreeNode | null;
    successor: UnitDependencyTreeNode | null;
}

const CONTINUATION_TARGET_KINDS = new Set([
    "definition",
    "content",
    "for",
    "import-document",
]);

function extractRefIdsFromText(text: string | undefined): Set<string> {
    const refs = new Set<string>();
    if (!text) return refs;
    const pattern = new RegExp(REF_ID_RE.source, "g");
    for (const match of text.matchAll(pattern)) {
        if (match[1]) refs.add(match[1]);
    }
    return refs;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function extractContinuationTargetRefs(configText: string | undefined): Set<string> {
    const refs = new Set<string>();
    if (!configText?.trim()) return refs;

    let config: unknown;
    try {
        config = JSON.parse(configText);
    } catch {
        return refs;
    }

    const seen = new WeakSet<object>();
    const visit = (value: unknown) => {
        if (!value || typeof value !== "object") return;
        if (seen.has(value)) return;
        seen.add(value);

        if (Array.isArray(value)) {
            for (const item of value) visit(item);
            return;
        }

        if (!isRecord(value)) return;
        const kind = value.kind;
        const id = value.id;
        if (
            typeof kind === "string" &&
            CONTINUATION_TARGET_KINDS.has(kind) &&
            typeof id === "string" &&
            id.trim()
        ) {
            refs.add(id);
        }
        for (const child of Object.values(value)) visit(child);
    };

    visit(config);
    return refs;
}

function extractConfigRefs(configText: string | undefined): Set<string> {
    const refs = extractRefIdsFromText(configText);
    for (const ref of extractContinuationTargetRefs(configText)) refs.add(ref);
    return refs;
}

function addUnit(
    units: Map<string, UnitDependencyNode>,
    unit: UnitDependencyNode,
) {
    if (units.has(unit.id)) return;
    units.set(unit.id, unit);
}

function resolveDependencyId(
    refId: string,
    allIds: ReadonlySet<string>,
    importNamespaces: ReadonlySet<string>,
): string | null {
    if (!refId || refId.startsWith("input/") || refId.startsWith("assets/")) {
        return null;
    }
    if (allIds.has(refId)) return refId;

    const privateAlias = resolveImportPrivateAliasId(refId, (candidate) =>
        allIds.has(candidate),
    );
    if (privateAlias) return privateAlias;

    const scopedRoot = resolveDependencyRootId(refId, (candidate) =>
        allIds.has(candidate),
    );
    if (scopedRoot) return scopedRoot;

    const slashIndex = refId.indexOf("/");
    if (slashIndex > 0) {
        const namespace = refId.slice(0, slashIndex);
        if (importNamespaces.has(namespace)) return namespace;
    }

    return null;
}

function addDependencyRefs(
    dependencies: Map<string, Set<string>>,
    fromId: string,
    refs: Iterable<string>,
    allIds: ReadonlySet<string>,
    importNamespaces: ReadonlySet<string>,
) {
    const bucket = dependencies.get(fromId);
    if (!bucket) return;
    for (const ref of refs) {
        const depId = resolveDependencyId(ref, allIds, importNamespaces);
        if (!depId || depId === fromId) continue;
        bucket.add(depId);
    }
}

function refsInsideLineRange(
    scan: DocumentScanResult,
    startLine: number,
    endLine: number,
): string[] {
    return scan.refs
        .filter((ref) => ref.line >= startLine && ref.line <= endLine)
        .map((ref) => ref.targetId);
}

function isNestedInForBlock(
    scan: DocumentScanResult,
    startLine: number,
    endLine: number,
): boolean {
    return scan.forBlocks.some(
        (block) => block.startLine < startLine && endLine <= block.endLine,
    );
}

function isNestedInTemplateContent(
    scan: DocumentScanResult,
    startLine: number,
    endLine: number,
): boolean {
    return scan.contentDefines.some(
        (content) =>
            !!content.template &&
            content.startLine < startLine &&
            endLine < content.endLine,
    );
}

function lineRangeUsesLoopVariableRef(
    lines: readonly string[],
    startLine: number,
    endLine: number,
): boolean {
    let insideOutputFence = false;
    let outputFenceLength = 3;

    for (let line = startLine; line <= endLine; line += 1) {
        const text = lines[line] ?? "";
        const trimmed = text.trim();
        const fenceMatch = /^(`{3,}|~{3,})([A-Za-z0-9_-]*)/.exec(trimmed);

        if (insideOutputFence) {
            if (
                trimmed.length === outputFenceLength &&
                /^[`~]+$/.test(trimmed)
            ) {
                insideOutputFence = false;
            }
            continue;
        }

        if (fenceMatch?.[2] === "output") {
            insideOutputFence = true;
            outputFenceLength = fenceMatch[1].length;
            continue;
        }

        if (/\$ref\{\$[^}]+\}/.test(text)) return true;
    }

    return false;
}

function isRunnableUnit(
    scan: DocumentScanResult,
    lines: readonly string[],
    unit: {
        kind: UnitDependencyKind;
        startLine: number;
        endLine: number;
        template?: boolean;
    },
): boolean {
    if (unit.template) return false;
    if (isNestedInTemplateContent(scan, unit.startLine, unit.endLine)) return false;

    const insideFor = isNestedInForBlock(scan, unit.startLine, unit.endLine);
    if (!insideFor) return true;

    if (unit.kind === "definition") return false;
    return !lineRangeUsesLoopVariableRef(lines, unit.startLine, unit.endLine);
}

export function canRunUnitDependencyNode(
    node: UnitDependencyTreeNode,
): boolean {
    return node.unit.runnable !== false;
}

export function buildUnitDependencyGraphFromSource(
    source: string,
): UnitDependencyGraph {
    const scan = scanDocumentText(source);
    const lines = source.split("\n");
    const units = new Map<string, UnitDependencyNode>();

    for (const def of scan.defines) {
        addUnit(units, {
            id: def.id,
            kind: "definition",
            line: def.startLine + 1,
            detail: def.pluginName,
            runnable: isRunnableUnit(scan, lines, {
                kind: "definition",
                startLine: def.startLine,
                endLine: def.endLine,
                template: def.template,
            }),
        });
    }
    for (const forBlock of scan.forBlocks) {
        addUnit(units, {
            id: forBlock.id,
            kind: "for",
            line: forBlock.startLine + 1,
            detail: forBlock.each ? `each=${forBlock.each}` : "for",
            runnable: isRunnableUnit(scan, lines, {
                kind: "for",
                startLine: forBlock.startLine,
                endLine: forBlock.endLine,
                template: forBlock.template,
            }),
        });
    }
    for (const content of scan.contentDefines) {
        addUnit(units, {
            id: content.id,
            kind: "content",
            line: content.startLine + 1,
            detail: "content",
            runnable: isRunnableUnit(scan, lines, {
                kind: "content",
                startLine: content.startLine,
                endLine: content.endLine,
                template: content.template,
            }),
        });
    }
    for (const imp of scan.imports) {
        addUnit(units, {
            id: imp.namespace,
            kind: "import",
            line: imp.line + 1,
            detail: imp.source,
            runnable: true,
        });
    }
    for (const use of scan.useBlocks) {
        addUnit(units, {
            id: use.id,
            kind: "use",
            line: use.startLine + 1,
            detail: `from=${use.from}`,
            runnable: isRunnableUnit(scan, lines, {
                kind: "use",
                startLine: use.startLine,
                endLine: use.endLine,
                template: use.template,
            }),
        });
    }

    const allIds = new Set(units.keys());
    const importNamespaces = new Set(scan.imports.map((imp) => imp.namespace));
    const dependencies = new Map<string, Set<string>>();
    for (const id of allIds) dependencies.set(id, new Set());

    for (const def of scan.defines) {
        addDependencyRefs(
            dependencies,
            def.id,
            extractConfigRefs(def.config),
            allIds,
            importNamespaces,
        );
    }

    for (const forBlock of scan.forBlocks) {
        const refs = new Set([
            ...extractRefIdsFromText(forBlock.of),
            ...refsInsideLineRange(scan, forBlock.startLine, forBlock.endLine),
        ]);
        addDependencyRefs(
            dependencies,
            forBlock.id,
            refs,
            allIds,
            importNamespaces,
        );
    }

    for (const content of scan.contentDefines) {
        addDependencyRefs(
            dependencies,
            content.id,
            extractRefIdsFromText(content.body),
            allIds,
            importNamespaces,
        );
    }

    for (const use of scan.useBlocks) {
        const refs = new Set([
            use.from,
            ...extractConfigRefs(use.config),
        ]);
        addDependencyRefs(
            dependencies,
            use.id,
            refs,
            allIds,
            importNamespaces,
        );
    }

    const dependents = new Map<string, Set<string>>();
    for (const id of allIds) dependents.set(id, new Set());
    for (const [fromId, dependencyIds] of dependencies) {
        for (const dependencyId of dependencyIds) {
            dependents.get(dependencyId)?.add(fromId);
        }
    }

    return { units, dependencies, dependents };
}

function sortedUnitIds(
    graph: UnitDependencyGraph,
    ids: Iterable<string>,
): string[] {
    return [...ids].sort((a, b) => {
        const lineA = graph.units.get(a)?.line ?? Number.MAX_SAFE_INTEGER;
        const lineB = graph.units.get(b)?.line ?? Number.MAX_SAFE_INTEGER;
        return lineA - lineB || a.localeCompare(b);
    });
}

function resolveTargetUnitId(
    graph: UnitDependencyGraph,
    targetId: string,
): string | null {
    const allIds = new Set(graph.units.keys());
    return graph.units.has(targetId)
        ? targetId
        : resolveDependencyRootId(targetId, (candidate) => allIds.has(candidate))
            ?? resolveImportPrivateAliasId(targetId, (candidate) => allIds.has(candidate));
}

function buildTreeNode(
    graph: UnitDependencyGraph,
    unitId: string,
    getChildIds: (graph: UnitDependencyGraph, unitId: string) => Iterable<string>,
    activePath: Set<string>,
    seen: Set<string>,
): UnitDependencyTreeNode | null {
    const unit = graph.units.get(unitId);
    if (!unit) return null;

    if (activePath.has(unitId)) {
        return { unit, children: [], cycle: true };
    }

    const repeated = seen.has(unitId);
    if (repeated) {
        return { unit, children: [], repeated: true };
    }

    seen.add(unitId);
    activePath.add(unitId);
    const children = sortedUnitIds(graph, getChildIds(graph, unitId))
        .map((childId) => buildTreeNode(
            graph,
            childId,
            getChildIds,
            activePath,
            seen,
        ))
        .filter((child): child is UnitDependencyTreeNode => child !== null);
    activePath.delete(unitId);

    return { unit, children };
}

function buildTreeFromGraph(
    graph: UnitDependencyGraph,
    targetId: string,
    getChildIds: (graph: UnitDependencyGraph, unitId: string) => Iterable<string>,
): UnitDependencyTreeNode | null {
    if (!targetId) return null;
    const resolvedTargetId = resolveTargetUnitId(graph, targetId);
    if (!resolvedTargetId) return null;
    return buildTreeNode(
        graph,
        resolvedTargetId,
        getChildIds,
        new Set(),
        new Set(),
    );
}

export function buildUnitDependencyTreesFromSource(
    source: string,
    targetId: string,
): UnitDependencyTrees {
    if (!source || !targetId) {
        return { predecessor: null, successor: null };
    }
    const graph = buildUnitDependencyGraphFromSource(source);
    return {
        predecessor: buildTreeFromGraph(
            graph,
            targetId,
            (item, unitId) => item.dependencies.get(unitId) ?? [],
        ),
        successor: buildTreeFromGraph(
            graph,
            targetId,
            (item, unitId) => item.dependents.get(unitId) ?? [],
        ),
    };
}

export function buildPredecessorTreeFromSource(
    source: string,
    targetId: string,
): UnitDependencyTreeNode | null {
    if (!source || !targetId) return null;
    const graph = buildUnitDependencyGraphFromSource(source);
    return buildTreeFromGraph(
        graph,
        targetId,
        (item, unitId) => item.dependencies.get(unitId) ?? [],
    );
}

export function buildSuccessorTreeFromSource(
    source: string,
    targetId: string,
): UnitDependencyTreeNode | null {
    if (!source || !targetId) return null;
    const graph = buildUnitDependencyGraphFromSource(source);
    return buildTreeFromGraph(
        graph,
        targetId,
        (item, unitId) => item.dependents.get(unitId) ?? [],
    );
}

export function flattenUnitDependencyRows(
    nodes: readonly UnitDependencyTreeNode[],
    depth = 0,
): UnitDependencyDisplayRow[] {
    const rows: UnitDependencyDisplayRow[] = [];
    for (const node of nodes) {
        rows.push({ node, depth });
        rows.push(...flattenUnitDependencyRows(node.children, depth + 1));
    }
    return rows;
}

export function getPredecessorRowsForTarget(
    targetId: string,
    tree: UnitDependencyTreeNode,
): UnitDependencyDisplayRow[] {
    const roots = targetId === tree.unit.id ? tree.children : [tree];
    return flattenUnitDependencyRows(roots);
}

export function getSuccessorRowsForTarget(
    targetId: string,
    tree: UnitDependencyTreeNode,
): UnitDependencyDisplayRow[] {
    const roots = targetId === tree.unit.id ? tree.children : [tree];
    return flattenUnitDependencyRows(roots);
}

export function findHoverUnitIdInSource(
    source: string,
    line: number,
    column: number,
): string | null {
    if (!source || line < 1) return null;
    const lineIndex = line - 1;
    const scan = scanDocumentText(source);

    const render = scan.renders.find(
        (item) =>
            item.line === lineIndex &&
            column >= item.startChar &&
            column <= item.endChar,
    );
    if (render) return render.definitionId;

    const ref = scan.refs.find(
        (item) =>
            item.line === lineIndex &&
            column >= item.startChar &&
            column <= item.endChar,
    );
    if (ref) return ref.targetId;

    const unit =
        scan.defines.find((item) => lineIndex >= item.startLine && lineIndex <= item.endLine) ??
        scan.useBlocks.find((item) => lineIndex >= item.startLine && lineIndex <= item.endLine) ??
        scan.contentDefines.find((item) => lineIndex >= item.startLine && lineIndex <= item.endLine) ??
        scan.forBlocks.find((item) => lineIndex >= item.startLine && lineIndex <= item.endLine) ??
        scan.imports.find((item) => lineIndex >= item.line && lineIndex <= item.endLine);

    if (!unit) return null;
    if ("namespace" in unit) return unit.namespace;
    return unit.id;
}

export function findRenderTargetIdInSource(
    source: string,
    line: number,
    column: number,
): string | null {
    if (!source || line < 1) return null;
    const lineIndex = line - 1;
    const scan = scanDocumentText(source);
    const render = scan.renders.find(
        (item) =>
            item.line === lineIndex &&
            column >= item.startChar &&
            column <= item.endChar,
    );
    return render?.definitionId ?? null;
}
